import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileText } from 'lucide-react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable CSV/PDF column-picker dialog.
 * Props:
 *   open, onClose
 *   path           - saved-view path used to fetch default columns from backend
 *   format         - 'csv' | 'pdf' (controls primary button label / icon only)
 *   onConfirm      - ({columns: string}) => void  (parent triggers download)
 */
const ExportColumnPicker = ({ open, onClose, path, format = 'csv', onConfirm }) => {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !path) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API_URL}/api/saved-views/columns?path=${encodeURIComponent(path)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const cols = Array.isArray(data?.columns) ? data.columns : [];
        setAvailable(cols);
        setSelected(cols);  // start with everything selected
      } catch {
        setAvailable([]);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, path]);

  const toggle = (c) => {
    setSelected((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c]);
  };

  const allOn = selected.length === available.length && available.length > 0;

  const submit = () => {
    onConfirm({ columns: selected.join(',') });
    onClose();
  };

  const Icon = format === 'pdf' ? FileText : Download;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="export-column-picker">
        <DialogHeader>
          <DialogTitle>Export columns</DialogTitle>
          <DialogDescription>
            Choose which fields to include in the {format.toUpperCase()}.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          {loading ? (
            <p className="text-xs text-muted-foreground py-3 text-center">Loading…</p>
          ) : available.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">
              No exportable columns for this view.
            </p>
          ) : (
            <>
              <label className="flex items-center gap-2 cursor-pointer pb-2 border-b">
                <Checkbox
                  checked={allOn}
                  onCheckedChange={(c) => setSelected(c ? available : [])}
                  data-testid="export-cols-all"
                />
                <Label className="text-xs font-semibold cursor-pointer">
                  {allOn ? 'Deselect all' : 'Select all'}
                </Label>
              </label>
              <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                {available.map((c) => (
                  <label
                    key={c}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={selected.includes(c)}
                      onCheckedChange={() => toggle(c)}
                      data-testid={`export-col-${c}`}
                    />
                    <span className="truncate" title={c}>{c}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={selected.length === 0}
            className="bg-[#111565] hover:bg-[#111565]/90 gap-1"
            data-testid="export-cols-submit"
          >
            <Icon className="h-3.5 w-3.5" />
            Export {format.toUpperCase()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportColumnPicker;
